﻿import classNames from 'classnames';
import { Button, ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { Tooltip } from 'components/tooltip';
import { WizardSteps } from 'components/wizard';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './Wizard.css';

export function Wizard(
    {
        id = null,
        allowBack = true,
        canProceed = true,
        cantProceedTooltip = null,
        additionalStepInformation = [],
        showStepTitles = false,
        allowDirectStepNavigation = false,
        saveBetweenSteps = false,
        alwaysSaveBetweenSteps = false,
        showSaveButton = true,
        disabled = false,
        loading = false,
        alignButtonsRight = false,
        canFinish = true,
        isComplete = false,
        saveAction = () => null,
        footer = () => null,
        showFinishOnAllSteps = false,
        finishButtonText = 'Finish',
        initialStep = 0,
        additionalButtons,
        additionalButtonsStart,
        children,
        onFinish,
        onStepChange
    }
) {
	const [currentState, setCurrentState] = useState({
		step: initialStep,
		direction: "forward",
		previousStep: 0,
		stepTitle: ''
	});
	const [totalSteps, setTotalSteps] = useState(0);

	const isDataDirty = useSelector(state => state.dirtyData.dirty);

	useEffect(() => {
		var stepsCount = children.filter(x => x != null && x !== false).length;
		setTotalSteps(stepsCount);

		setCurrentState({
			...currentState,
			stepTitle: getStepTitle(currentState.step)
		});
	}, [children]);

	useEffect(onStepChangeValue, [currentState.step]);

	function onStepChangeValue() {
		if (totalSteps === 0) {
			return;
		}

		onStepChange(currentState.step, currentState.direction, currentState.previousStep);
	}

	function onNextStep() {
		moveToStep(currentState.step + 1);
	}

	function onPreviousStep() {
		moveToStep(currentState.step - 1);
	}

	function onStepClick(step) {
		if (allowDirectStepNavigation) {
			moveToStep(step);
		}
	}

	function moveToStep(step) {
		const updateState = () => {
			setCurrentState((state) => {
				return {
					...state,
					step: step,
					direction: step >= state.step ? "forward" : "backward",
					previousStep: step - 1,
					stepTitle: getStepTitle(step)
				};
			});
		};

		// A step action is skipped on a clean page, so a page whose action must always run — an
		// upload that gates the next step, say — sets alwaysSaveBetweenSteps.
		if (saveBetweenSteps && saveAction && (alwaysSaveBetweenSteps || isDataDirty)) {
			saveAction(step, onNextStep, onPreviousStep).then(updateState);
		} else {
			updateState();
		}
	}

	function getStepTitle(stepIndex) {
		let stepTitle = '';

		if (additionalStepInformation && stepIndex < additionalStepInformation.length) {
			stepTitle = additionalStepInformation[stepIndex].stepTitle;
		}

		return stepTitle;
	}

	function renderNextButton() {
		return (
            <Button
				icon="double-chevron-right"
				intent="primary"
				loading={loading}
				text="Next"
				onClick={onNextStep}
				disabled={disabled || !canProceed}
				id="next-btn"
			/>
        );
	}

	function renderFinishButton() {
		return (<ButtonSave intent="primary" loading={loading} text={finishButtonText} onClick={onFinish} disabled={disabled} simpleDisabled={!canProceed} id="finish-btn" />);
	}

	return (
        <div id={id} className="wizard">
            <WizardSteps
				currentStep={currentState.step}
				totalSteps={totalSteps}
				additionalStepInformation={additionalStepInformation}
				showStepTitles={showStepTitles}
				onStepClick={onStepClick}
				allowDirectStepNavigation={allowDirectStepNavigation}
				loading={loading}
				wizardComplete={isComplete}
				minimal
			/>
            {children.map((child, i) => {
				if (i === currentState.step) {
					return child;
				}
				return null;
			})}
            <div>
				{footer()}
			</div>
            <div className={classNames('inline-items', { "inline-items-space": !alignButtonsRight, "inline-items-end": alignButtonsRight, "inline-items-space-reverse": alignButtonsRight && additionalButtons != null })}>
				<div className="button-row">
					{additionalButtonsStart}
					<ShowHide
						evaluator={allowBack && currentState.step > 0}
						show={(
							<Button icon="double-chevron-left" intent="primary" loading={loading} text="Previous" onClick={onPreviousStep} disabled={disabled} id="prev-btn"/>
						)}
					/>
					<ShowHide
						evaluator={currentState.step < (totalSteps - 1)}
						show={
							(canProceed || !cantProceedTooltip)
								? renderNextButton()
								: <Tooltip
									content={cantProceedTooltip}
									position='left'
								>
									{renderNextButton()}
								</Tooltip>
						}
					/>
					<ShowHide
						evaluator={(showFinishOnAllSteps || currentState.step >= (totalSteps - 1)) && canFinish}
						show={(
							(canProceed || !cantProceedTooltip)
								? renderFinishButton()
								: <Tooltip
									content={cantProceedTooltip}
									position='left'
								>
									{renderFinishButton()}
								</Tooltip>
						)}
					/>
				</div>
				<div className="button-row">
					<ShowHide
						evaluator={saveBetweenSteps && showSaveButton}
						show={(
							<ButtonSave intent="primary" loading={loading} text="Save" disabled={disabled} onClick={saveAction} id="save-btn" />
						)}
					/>
					{additionalButtons}
				</div>
			</div>
        </div>
    );
}

Wizard.propTypes = {
	id: PropTypes.string,
	allowBack: PropTypes.bool,
	canProceed: PropTypes.bool,
	cantProceedTooltip: PropTypes.string,
	onStepChange: PropTypes.func.isRequired,
	onFinish: PropTypes.func.isRequired,
	additionalStepInformation: PropTypes.array,
	showStepTitles: PropTypes.bool,
	allowDirectStepNavigation: PropTypes.bool,
	saveBetweenSteps: PropTypes.bool,
	alwaysSaveBetweenSteps: PropTypes.bool,
	showSaveButton: PropTypes.bool,
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	additionalButtonsStart: PropTypes.node,
	additionalButtons: PropTypes.node,
	alignButtonsRight: PropTypes.bool,
	canFinish: PropTypes.bool,
	isComplete: PropTypes.bool,
	saveAction: PropTypes.func,
	footer: PropTypes.func,
	showFinishOnAllSteps: PropTypes.bool,
	finishButtonText: PropTypes.string,
	initialStep: PropTypes.number,
	children: PropTypes.node
};
